import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { QRCodeSVG } from 'qrcode.react';
import './Inzetmiddelen.css';
import InzetAanvragen from '../components/InzetAanvragen';
import InzetPartijen from '../components/InzetPartijen';
import InzetAgenda from '../components/InzetAgenda';

const STATUS_LABEL = {
  beschikbaar: 'Beschikbaar', uit: 'Uit', controle: 'Controle',
  geblokkeerd: 'Geblokkeerd', wacht_vullen: 'Wacht op vullen',
};
const STATUS_KLEUR = {
  beschikbaar: '#059669', uit: '#2563eb', controle: '#d97706',
  geblokkeerd: '#dc2626', wacht_vullen: '#7c3aed',
};
const STATUS_BG = {
  beschikbaar: '#d1fae5', uit: '#dbeafe', controle: '#fef3c7',
  geblokkeerd: '#fee2e2', wacht_vullen: '#ede9fe',
};

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };

export default function Inzetmiddelen() {
  const { hasPermission } = useAuth();
  const magBeheer = hasPermission('inzetmiddelen:beheer');
  const magReserveren = hasPermission('inzetmiddelen:reserveren');

  const [types, setTypes] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showType, setShowType] = useState(false);
  const [unitsVoor, setUnitsVoor] = useState(null);
  const [planVoor, setPlanVoor] = useState(null);
  const [monteurs, setMonteurs] = useState([]);
  const [qrVoor, setQrVoor] = useState(null);
  const [projecten, setProjecten] = useState([]);

  const laden = useCallback(async () => {
    try {
      const [t, u, m] = await Promise.all([
        api.get('/inzetmiddelen/types'),
        api.get('/inzetmiddelen'),
        api.get('/inzetmiddelen/monteurs-lijst'),
      ]);
      setTypes(t.data.types || []);
      setUnits(u.data.units || []);
      setMonteurs(m.data.monteurs || []);
      setError(null);
    } catch (e) { setError('Kon inzetmiddelen niet laden.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { laden(); }, [laden]);
  useEffect(() => { api.get('/projecten').then(r => setProjecten(r.data.projecten || [])).catch(() => {}); }, []);
  const num = (v) => Number(v || 0);

  const verwijderUnit = async (u) => {
    if (!window.confirm('Unit ' + u.code + ' verwijderen?')) return;
    try { await api.delete('/inzetmiddelen/' + u.id); laden(); }
    catch (e) { alert(e.response?.data?.message || 'Verwijderen mislukt'); }
  };

  const vrijgeven = async (u) => {
    if (!window.confirm(u.code + ' vrijgeven (weer beschikbaar maken)?')) return;
    try {
      // vrijgeven via scan-endpoint vereist token; gebruik unit-id route
      await api.post('/inzetmiddelen/' + u.id + '/vrijgeven');
      laden();
    } catch (e) { alert(e.response?.data?.message || 'Vrijgeven mislukt'); }
  };

  if (loading) return <div className="loading">Laden...</div>;
  if (error)   return <div className="inzet-page"><p style={{ color: '#dc2626' }}>{error}</p></div>;

  return (
    <div className="inzet-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Inzetmiddelen</h1>
          <p className="inzet-sub">Pool, beschikbaarheid en status per type.</p>
        </div>
        <div>{magBeheer && <button className="btn-primary" onClick={() => setShowType(true)}>+ Nieuw type</button>}<InzetPartijen magBeheer={magBeheer} /></div>
      </div>

      <InzetAanvragen magBeheer={magBeheer} />
      <InzetAgenda types={types} />
      {types.length === 0 ? (
        <div className="inzet-empty">Nog geen types aangemaakt.</div>
      ) : (
        <div className="inzet-grid">
          {types.map((t) => (
            <div key={t.id} className="inzet-card">
              <div className="ic-top">
                <span className="ic-naam">{t.naam}</span>
                <span className="ic-totaal">{num(t.totaal)} totaal</span>
              </div>
              <div className="ic-meta">
                {t.tracking_mode === 'persoon' ? 'volgt op houder' : 'volgt op locatie'}
                {t.bijvullen ? ' · bijvullen' : ''}
                {t.vereist_controle ? ' · controle' : ''}
                {t.uniek_per_unit ? ' · uniek' : ''}
                {num(t.omsteltijd_dagen) > 0 ? ` · omsteltijd ${t.omsteltijd_dagen}d` : ''}
              </div>
              <div className="inzet-badges">
                {['beschikbaar','uit','controle','geblokkeerd','wacht_vullen'].map((s) => (
                  num(t[s]) > 0 ? (
                    <span key={s} className="inzet-badge" style={{ background: STATUS_BG[s], color: STATUS_KLEUR[s] }}>
                      {STATUS_LABEL[s]}: {num(t[s])}
                    </span>
                  ) : null
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                {magReserveren && (
                  <button className="inzet-btn-light" onClick={() => setPlanVoor(t)}>Planning</button>
                )}
                {magBeheer && (
                  <button className="inzet-btn-light" onClick={() => setUnitsVoor(t)}>+ Units</button>
                )}
                {magBeheer && (
                  <button className="inzet-btn-light" onClick={() => setQrVoor(t)}>QR's</button>
                )}
                {magBeheer && (
                  <div style={{ width: '100%', marginTop: 6 }}>
                    <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 2 }}>Vaste monteur (orders)</label>
                    <select className="inzet-input" style={{ fontSize: 13 }} value={t.vaste_monteur_id || ''}
                      onChange={async (e) => {
                        const mid = e.target.value ? Number(e.target.value) : null;
                        try { await api.patch('/inzetmiddelen/types/' + t.id + '/monteur', { monteur_id: mid }); laden(); }
                        catch (err) { alert('Opslaan mislukt'); }
                      }}>
                      <option value="">— planner wijst toe —</option>
                      {monteurs.map(m => <option key={m.id} value={m.id}>{m.naam || m.gebruikersnaam}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <h2 className="inzet-section-title">Units ({units.length})</h2>
      {units.length === 0 ? (
        <div className="inzet-empty">Nog geen units in de pool.</div>
      ) : (
        <div className="inzet-table-wrap">
          <table className="inzet-table">
            <thead>
              <tr><th>Code</th><th>Type</th><th>Status</th><th>Locatie / houder</th>
                {magBeheer && <th style={{ width: 60 }}></th>}</tr>
            </thead>
            <tbody>
              {units.map((u) => (
                <tr key={u.id}>
                  <td>{u.code}</td><td>{u.type_naam}</td>
                  <td><span className="inzet-status-dot" style={{ color: STATUS_KLEUR[u.status] || '#6b7280' }}>
                    {STATUS_LABEL[u.status] || u.status}</span></td>
                  <td style={{ color: '#6b7280' }}>
                    {u.houder_naam || u.laatste_plek || u.magazijn_naam || '-'}
                    {u.laatste_lat && u.laatste_lng && (
                      <a href={`https://www.google.com/maps?q=${u.laatste_lat},${u.laatste_lng}`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 8, color: '#2563eb', fontSize: 13, textDecoration: 'none' }}>📍 Kaart</a>
                    )}
                    {u.status === 'geblokkeerd' && u.laatste_opmerking && (
                      <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 2 }}>{u.laatste_opmerking}</div>
                    )}
                    {magReserveren && (u.status === 'geblokkeerd' || u.status === 'controle') && (
                      <button className="inzet-btn-light" style={{ marginTop: 6, fontSize: 12, color: '#059669' }}
                        onClick={() => vrijgeven(u)}>Vrijgeven</button>
                    )}
                  </td>
                  {magBeheer && (
                    <td><button className="inzet-btn-del" title="Verwijderen" onClick={() => verwijderUnit(u)}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                    </button></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showType && <TypeModal onClose={() => setShowType(false)} onSaved={() => { setShowType(false); laden(); }} />}
      {unitsVoor && <UnitsModal type={unitsVoor} onClose={() => setUnitsVoor(null)} onSaved={() => { setUnitsVoor(null); laden(); }} />}
      {planVoor && <PlanningModal type={planVoor} projecten={projecten} onClose={() => setPlanVoor(null)} />}
      {qrVoor && <StickerModal type={qrVoor} onClose={() => setQrVoor(null)} />}
    </div>
  );
}

function TypeModal({ onClose, onSaved }) {
  const [naam, setNaam] = useState('');
  const [mode, setMode] = useState('locatie');
  const [uniek, setUniek] = useState(false);
  const [controle, setControle] = useState(false);
  const [bijvullen, setBijvullen] = useState(false);
  const [magazijn, setMagazijn] = useState(false);
  const [label, setLabel] = useState('Defect');
  const [omstel, setOmstel] = useState('0');
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(null);

  const opslaan = async () => {
    if (!naam.trim()) { setFout('Naam is verplicht'); return; }
    setBezig(true); setFout(null);
    try {
      await api.post('/inzetmiddelen/types', {
        naam, tracking_mode: mode, uniek_per_unit: uniek, vereist_controle: controle,
        bijvullen, heeft_magazijn: magazijn, afwijking_label: label,
        omsteltijd_dagen: Number(omstel) || 0,
      });
      onSaved();
    } catch (e) { setFout(e.response?.data?.message || 'Opslaan mislukt'); setBezig(false); }
  };

  const Check = ({ v, set, lab }) => (
    <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '6px 0', cursor: 'pointer' }}>
      <input type="checkbox" checked={v} onChange={(e) => set(e.target.checked)} /> {lab}
    </label>
  );

  return (
    <div className="inzet-overlay">
      <div className="inzet-modal" onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Nieuw type</h3>
        <label className="inzet-lbl">Naam</label>
        <input className="inzet-input" value={naam} onChange={(e) => setNaam(e.target.value)}
          placeholder="bv. Poortje, Container, Gereedschap" autoFocus />
        <label className="inzet-lbl">Volgt op</label>
        <select className="inzet-input" value={mode} onChange={(e) => setMode(e.target.value)}>
          <option value="locatie">Locatie (poortjes, containers op plek)</option>
          <option value="persoon">Persoon (uitgeleend aan monteur)</option>
        </select>
        <div style={{ margin: '12px 0' }}>
          <Check v={uniek} set={setUniek} lab="Uniek per unit (eigen naam, bv. container)" />
          <Check v={controle} set={setControle} lab="Controle bij terugkomst (wvb keurt)" />
          <Check v={bijvullen} set={setBijvullen} lab="Bijvullen door magazijn" />
          <Check v={magazijn} set={setMagazijn} lab="Heeft magazijn (thuisbasis)" />
        </div>
        <label className="inzet-lbl">Omsteltijd (dagen na gebruik tot weer beschikbaar)</label>
        <input className="inzet-input" type="number" min="0" value={omstel}
          onChange={(e) => setOmstel(e.target.value)} />
        <label className="inzet-lbl">Afwijking-label</label>
        <input className="inzet-input" value={label} onChange={(e) => setLabel(e.target.value)}
          placeholder="bv. Defect, Schoonmaken nodig" />
        {fout && <p style={{ color: '#dc2626', fontSize: 13 }}>{fout}</p>}
        <div className="inzet-modal-btns">
          <button className="inzet-btn-light" onClick={onClose}>Annuleren</button>
          <button className="btn-primary" disabled={bezig} onClick={opslaan}>{bezig ? 'Opslaan...' : 'Opslaan'}</button>
        </div>
      </div>
    </div>
  );
}

function UnitsModal({ type, onClose, onSaved }) {
  const [aantal, setAantal] = useState(type.uniek_per_unit ? '' : '10');
  const [prefix, setPrefix] = useState(type.naam.slice(0, 6));
  const [code, setCode] = useState('');
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(null);
  const losMode = type.uniek_per_unit;

  const opslaan = async () => {
    setBezig(true); setFout(null);
    try {
      const body = { type_id: type.id };
      if (losMode) {
        if (!code.trim()) { setFout('Geef een naam/code'); setBezig(false); return; }
        body.code = code.trim();
      } else {
        if (!Number(aantal) || Number(aantal) < 1) { setFout('Geef een aantal'); setBezig(false); return; }
        body.aantal = Number(aantal); body.prefix = prefix.trim() || 'U';
      }
      await api.post('/inzetmiddelen', body);
      onSaved();
    } catch (e) { setFout(e.response?.data?.message || 'Opslaan mislukt'); setBezig(false); }
  };

  return (
    <div className="inzet-overlay">
      <div className="inzet-modal" onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Units toevoegen — {type.naam}</h3>
        {losMode ? (
          <>
            <label className="inzet-lbl">Naam / code (uniek)</label>
            <input className="inzet-input" value={code} onChange={(e) => setCode(e.target.value)}
              placeholder="bv. Container 3" autoFocus />
          </>
        ) : (
          <>
            <label className="inzet-lbl">Aantal</label>
            <input className="inzet-input" type="number" min="1" value={aantal}
              onChange={(e) => setAantal(e.target.value)} autoFocus />
            <label className="inzet-lbl">Prefix (codes worden Prefix-001, -002, ...)</label>
            <input className="inzet-input" value={prefix} onChange={(e) => setPrefix(e.target.value)} />
            <p style={{ fontSize: 12, color: '#6b7280' }}>Telt door vanaf het huidige aantal.</p>
          </>
        )}
        {fout && <p style={{ color: '#dc2626', fontSize: 13 }}>{fout}</p>}
        <div className="inzet-modal-btns">
          <button className="inzet-btn-light" onClick={onClose}>Annuleren</button>
          <button className="btn-primary" disabled={bezig} onClick={opslaan}>{bezig ? 'Opslaan...' : 'Toevoegen'}</button>
        </div>
      </div>
    </div>
  );
}

function PlanningModal({ type, projecten, onClose }) {
  const [maand, setMaand] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selStart, setSelStart] = useState(null);
  const [selEnd, setSelEnd] = useState(null);
  const [aantal, setAantal] = useState('1');
  const [projectId, setProjectId] = useState('');
  const [bestemming, setBestemming] = useState('');
  const [dagMap, setDagMap] = useState({});
  const [reserveringen, setReserveringen] = useState([]);
  const [waarschuwing, setWaarschuwing] = useState(null);
  const [capaciteit, setCapaciteit] = useState(0);
  const [bezig, setBezig] = useState(false);

  const ymd = (d) => {
    const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,'0'), dd = String(d.getDate()).padStart(2,'0');
    return `${y}-${m}-${dd}`;
  };
  const vanMaand = ymd(new Date(maand.getFullYear(), maand.getMonth(), 1));
  const totMaand = ymd(new Date(maand.getFullYear(), maand.getMonth()+1, 0));

  const laad = useCallback(async () => {
    try {
      const b = await api.get(`/inzetmiddelen/beschikbaarheid?type_id=${type.id}&van=${vanMaand}&tot=${totMaand}`);
      const map = {};
      (b.data.dagen || []).forEach(d => { map[d.dag.slice(0,10)] = d; });
      setDagMap(map); setCapaciteit(b.data.capaciteit || 0);
      const r = await api.get(`/inzetmiddelen/reserveringen?type_id=${type.id}&van=${vanMaand}&tot=${totMaand}`);
      setReserveringen(r.data.reserveringen || []);
    } catch (e) {}
  }, [type.id, vanMaand, totMaand]);
  useEffect(() => { laad(); }, [laad]);

  // kalender-grid: begin op maandag
  const dagen = [];
  const eerste = new Date(maand.getFullYear(), maand.getMonth(), 1);
  let offset = (eerste.getDay() + 6) % 7; // ma=0
  for (let i = 0; i < offset; i++) dagen.push(null);
  const aantalDagen = new Date(maand.getFullYear(), maand.getMonth()+1, 0).getDate();
  for (let d = 1; d <= aantalDagen; d++) dagen.push(new Date(maand.getFullYear(), maand.getMonth(), d));

  const inRange = (d) => {
    if (!selStart) return false;
    const s = selStart, e = selEnd || selStart;
    const lo = s < e ? s : e, hi = s < e ? e : s;
    return d >= lo && d <= hi;
  };

  const klikDag = (d) => {
    if (!selStart || (selStart && selEnd)) { setSelStart(d); setSelEnd(null); }
    else { if (d < selStart) { setSelEnd(selStart); setSelStart(d); } else setSelEnd(d); }
  };

  const reserveer = async () => {
    if (!selStart) { setWaarschuwing('Selecteer eerst dagen in de kalender'); return; }
    if (!Number(aantal) || Number(aantal) < 1) return;
    const s = selStart, e = selEnd || selStart;
    const lo = s < e ? s : e, hi = s < e ? e : s;
    setBezig(true); setWaarschuwing(null);
    try {
      const r = await api.post('/inzetmiddelen/reserveringen', {
        type_id: type.id, van_datum: ymd(lo), tot_datum: ymd(hi),
        aantal: Number(aantal), project_id: projectId ? Number(projectId) : null,
        bestemming: bestemming.trim() || null,
      });
      setWaarschuwing(r.data.waarschuwing || null);
      setSelStart(null); setSelEnd(null);
      laad();
    } catch (e) { setWaarschuwing(e.response?.data?.message || 'Reserveren mislukt'); }
    finally { setBezig(false); }
  };

  const annuleer = async (id) => {
    if (!window.confirm('Reservering annuleren?')) return;
    try { await api.delete('/inzetmiddelen/reserveringen/' + id); laad(); } catch (e) {}
  };

  const kleur = (info, sel) => {
    if (sel) return { background: '#bfdbfe', color: '#1e40af' };
    if (!info) return { background: '#f3f4f6', color: '#9ca3af' };
    if (info.tekort) return { background: '#fee2e2', color: '#b91c1c' };
    const ratio = capaciteit ? info.vrij / capaciteit : 1;
    if (ratio <= 0.3) return { background: '#fef3c7', color: '#92400e' };
    return { background: '#d1fae5', color: '#065f46' };
  };

  const maandNaam = maand.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
  const dagLabel = (d) => new Date(d).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' });

  return (
    <div className="inzet-overlay">
      <div className="inzet-modal" style={{ width: 560 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Planning — {type.naam}</h3>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <button className="inzet-btn-light" onClick={() => setMaand(new Date(maand.getFullYear(), maand.getMonth()-1, 1))}>◀</button>
          <strong style={{ textTransform: 'capitalize' }}>{maandNaam}</strong>
          <button className="inzet-btn-light" onClick={() => setMaand(new Date(maand.getFullYear(), maand.getMonth()+1, 1))}>▶</button>
        </div>

        <div className="inzet-cal-head">
          {['ma','di','wo','do','vr','za','zo'].map(d => <div key={d}>{d}</div>)}
        </div>
        <div className="inzet-cal">
          {dagen.map((d, i) => {
            if (!d) return <div key={i} />;
            const info = dagMap[ymd(d)];
            const sel = inRange(d);
            return (
              <div key={i} className="inzet-cal-day" style={kleur(info, sel)}
                title={info ? `${info.vrij} vrij van ${capaciteit}` : ''}
                onClick={() => klikDag(d)}>
                <span className="inzet-cal-dnum">{d.getDate()}</span>
                {info && <span className="inzet-cal-free">{info.vrij}</span>}
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginTop: 14, flexWrap: 'wrap' }}>
          <div style={{ width: 80 }}><label className="inzet-lbl">Aantal</label>
            <input className="inzet-input" type="number" min="1" value={aantal} onChange={(e) => setAantal(e.target.value)} /></div>
          <div style={{ flex: 1, minWidth: 200 }}><label className="inzet-lbl">Bestemming (waar heen?)</label>
            <input className="inzet-input" type="text" value={bestemming} onChange={(e) => setBestemming(e.target.value)} placeholder="Bijv. Stationsplein 4, Purmerend" /></div>
          <div style={{ flex: 1, minWidth: 160 }}><label className="inzet-lbl">Project (optioneel)</label>
            <select className="inzet-input" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">— geen —</option>
              {projecten.map(p => <option key={p.id} value={p.id}>{p.naam || p.titel || p.projectnaam || ('Project ' + p.id)}</option>)}
            </select></div>
          <button className="btn-primary" disabled={bezig} onClick={reserveer} style={{ height: 38 }}>
            {bezig ? '...' : 'Reserveer'}</button>
        </div>
        <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
          {selStart ? `Geselecteerd: ${dagLabel(selStart)}${selEnd ? ' – ' + dagLabel(selEnd) : ''}` : 'Klik een begindag en een einddag in de kalender.'}
        </p>

        {waarschuwing && (
          <div style={{ marginTop: 8, padding: '8px 12px', background: '#fef3c7', color: '#92400e',
            borderRadius: 8, fontSize: 13 }}>{waarschuwing}</div>
        )}

        {reserveringen.length > 0 && (
          <>
            <label className="inzet-lbl" style={{ marginTop: 14 }}>Reserveringen deze maand</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {reserveringen.map((r) => (
                <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontSize: 13, padding: '6px 10px', background: '#f9fafb', borderRadius: 8 }}>
                  <span><b>{r.aantal}×</b> {dagLabel(r.van_datum)} – {dagLabel(r.tot_datum)}
                    {r.afdeling_naam ? ` · ${r.afdeling_naam}` : ''}</span>
                  <button className="inzet-btn-light" onClick={() => annuleer(r.id)} style={{ fontSize: 12, color: '#dc2626' }}>annuleren</button>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="inzet-modal-btns">
          <button className="inzet-btn-light" onClick={onClose}>Sluiten</button>
        </div>
      </div>
    </div>
  );
}


function StickerModal({ type, onClose }) {
  const [units, setUnits] = useState([]);
  const [bezig, setBezig] = useState(true);
  const BASE = 'https://womsys.nl/monteurs-app?inzetmiddel=';

  useEffect(() => {
    api.get('/inzetmiddelen?type_id=' + type.id)
      .then(r => setUnits(r.data.units || []))
      .catch(() => {})
      .finally(() => setBezig(false));
  }, [type.id]);

  const print = () => {
    const w = window.open('', '_blank');
    if (!w) { alert('Sta pop-ups toe om te printen.'); return; }
    const svgs = document.querySelectorAll('#sticker-grid svg');
    let cells = '';
    units.forEach((u, i) => {
      const svg = svgs[i] ? svgs[i].outerHTML : '';
      cells += '<div class="cell">' + svg + '<div class="code">' + u.code + '</div></div>';
    });
    w.document.write('<html><head><title>Stickers ' + type.naam + '</title><style>' +
      'body{font-family:sans-serif;margin:12px}h2{font-size:16px}' +
      '.grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px}' +
      '.cell{border:1px solid #ccc;border-radius:8px;padding:10px;text-align:center;page-break-inside:avoid}' +
      '.cell svg{width:120px;height:120px}.code{margin-top:6px;font-size:13px;font-weight:600}' +
      '</style></head><body><h2>Stickers — ' + type.naam + '</h2><div class="grid">' + cells + '</div></body></html>');
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="inzet-overlay">
      <div className="inzet-modal" style={{ width: 640, maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>QR-stickers — {type.naam}</h3>
          <button className="btn-primary" onClick={print} disabled={units.length === 0}>Printen</button>
        </div>
        <p style={{ fontSize: 12, color: '#6b7280' }}>Plak elke sticker op het bijbehorende exemplaar. Scan met de monteurs-app.</p>
        {bezig ? <p>Laden...</p> : units.length === 0 ? <p>Nog geen units.</p> : (
          <div id="sticker-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 12 }}>
            {units.map((u) => (
              <div key={u.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, textAlign: 'center' }}>
                <QRCodeSVG value={BASE + u.qr_token} size={110} level="H" includeMargin={true} bgColor="#ffffff" fgColor="#1e40af" />
                <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600 }}>{u.code}</div>
              </div>
            ))}
          </div>
        )}
        <div className="inzet-modal-btns">
          <button className="inzet-btn-light" onClick={onClose}>Sluiten</button>
        </div>
      </div>
    </div>
  );
}

